// src/utils/axiosConfig.js
// Axios interceptor for automatic token refresh and logout
import axios from 'axios';
import {
    runSessionExpiredLogout,
    stashLogoutReasonFromApiData,
} from './sessionExpiredFlow';

let isRefreshing = false;
let failedQueue = [];
let requestInterceptorId = null;
let responseInterceptorId = null;

const log = (...args) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('[axiosConfig]', ...args);
    }
};

const processQueue = (error, token = null) => {
    log('prosessere kø:', failedQueue.length, 'forespørsel(er)', error ? 'med feil' : 'uten feil');
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

export const setupAxiosInterceptors = (logout, refreshToken) => {
    if (requestInterceptorId !== null) {
        log('eject request interceptor:', requestInterceptorId);
        axios.interceptors.request.eject(requestInterceptorId);
    }
    if (responseInterceptorId !== null) {
        log('eject response interceptor:', responseInterceptorId);
        axios.interceptors.response.eject(responseInterceptorId);
    }

    // Request interceptor - add withCredentials to all requests
    requestInterceptorId = axios.interceptors.request.use(
        (config) => {
            config.withCredentials = true; // Always send cookies
            return config;
        },
        (error) => Promise.reject(error)
    );
    log('request interceptor aktiv:', requestInterceptorId);

    // Response interceptor - handle 401 errors
    responseInterceptorId = axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            const errorCode = error.response?.data?.code;
            const errorMessage =
                error.response?.data?.error || error.response?.data?.message || '';
            const normalizedMessage = String(errorMessage).toLowerCase();

            // Don't intercept auth check or logout endpoints (prevents infinite loop)
            if (
                !originalRequest ||
                originalRequest.url?.includes('/auth/check') ||
                originalRequest.url?.includes('/auth/logout') ||
                originalRequest.url?.includes('/auth/refresh')
            ) {
                return Promise.reject(error);
            }

            // If error is 401 and we haven't tried refreshing yet
            if (error.response?.status === 401 && !originalRequest._retry) {
                log('401 mottatt:', {
                    url: originalRequest.url,
                    method: originalRequest.method,
                    code: errorCode || null,
                    message: errorMessage || null,
                });
                const shouldAttemptRefresh =
                    // Our middleware explicitly marks access-token expiry
                    errorCode === 'TOKEN_EXPIRED' ||
                    errorCode === 'ACCESS_TOKEN_MISSING' ||
                    // When the access-token cookie is missing/expired, middleware returns this
                    normalizedMessage === 'no token provided' ||
                    // Be defensive: some paths might return other wording
                    normalizedMessage === 'token expired';

                if (shouldAttemptRefresh) {
                    // Access token mangler eller er utløpt - prøv å refresh og retry en gang

                    // Mark retry up-front to avoid repeated refresh loops
                    originalRequest._retry = true;

                    if (isRefreshing) {
                        log('refresh paagaar allerede -> legger forespoersel i kø');
                        // Already refreshing - queue this request
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        })
                            .then(() => {
                                log('køet forespoersel retryes:', originalRequest.url);
                                return axios(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }

                    isRefreshing = true;
                    log('starter refresh-token flyt');

                    try {
                        // Attempt to refresh token
                        const refreshSuccess = await refreshToken();
                        log('refresh-token resultat:', refreshSuccess ? 'OK' : 'FEIL');

                        if (refreshSuccess) {
                            processQueue(null);
                            log('retry original forespoersel:', originalRequest.url);
                            return axios(originalRequest); // Retry original request
                        } else {
                            processQueue(error);
                            await runSessionExpiredLogout(logout);
                            return Promise.reject(error);
                        }
                    } catch (refreshError) {
                        processQueue(refreshError);
                        stashLogoutReasonFromApiData(refreshError.response?.data);
                        await runSessionExpiredLogout(logout);
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing = false;
                        log('refresh-flyt ferdig');
                    }
                } else {
                    // Hard auth-feil: samme flyt som refresh-feil (dialog først).
                    log('401 regnes som hard auth-feil -> runSessionExpiredLogout', {
                        url: originalRequest.url,
                        code: errorCode || null,
                        message: errorMessage || null,
                    });
                    stashLogoutReasonFromApiData(error.response?.data);
                    await runSessionExpiredLogout(logout);
                }
            }

            return Promise.reject(error);
        }
    );
};

export default axios;
